import * as React from 'react';
import { useState, useCallback } from 'react';
import { Alert } from 'reactstrap';
import { Box, Switch, FormControlLabel, Typography as MuiTypography, IconButton, Dialog, DialogTitle, DialogContent, Chip, ClickAwayListener } from '@mui/material';
import HelpIcon from '@material-ui/icons/Help';
import EditIcon from '@material-ui/icons/Edit';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { GeneralSettingsData, ClientIntelligentCategorizationSettings as CategorizationSettings, ClientTagRuleSettings, ClientTagRuleType } from '../../../models';
import styled from 'styled-components';
import { TextInputField } from '../../../components/Forms/fields/TextInputField';
import { ChromePicker } from 'react-color';

// Default colors for tag types
const DEFAULT_TAG_COLORS: Record<ClientTagRuleType, string> = {
    [ClientTagRuleType.Active]: '#4CAF50',      // Green
    [ClientTagRuleType.AtRisk]: '#FF9800',      // Orange
    [ClientTagRuleType.Inactive]: '#F44336',    // Red (will vary by days)
    [ClientTagRuleType.Debtor]: '#C62828',
};

// Get appropriate color based on rule type and days for inactive rules
const getDefaultColor = (rule: ClientTagRuleSettings): string => {
    if (rule.type === ClientTagRuleType.Inactive) {
        if (rule.days <= 90) return '#FFC107';   // Amber/Yellow
        if (rule.days <= 180) return '#FF5722';  // Deep Orange
        return '#F44336';                         // Red
    }
    return DEFAULT_TAG_COLORS[rule.type] || '#9E9E9E';
};

// Create default rules when enabling the feature for the first time
const createDefaultRules = (t: (key: any) => string): ClientTagRuleSettings[] => {
    return [
        {
            ruleId: 'active',
            enabled: true,
            tagValue: t(TK.clientTagActive),
            color: DEFAULT_TAG_COLORS[ClientTagRuleType.Active],
            type: ClientTagRuleType.Active,
            days: 60
        },
        {
            ruleId: 'atRisk',
            enabled: true,
            tagValue: t(TK.clientTagAtRisk),
            color: DEFAULT_TAG_COLORS[ClientTagRuleType.AtRisk],
            type: ClientTagRuleType.AtRisk,
            days: 60
        },
        {
            ruleId: 'inactive90',
            enabled: true,
            tagValue: t(TK.clientTagInactive90),
            color: '#FFC107',
            type: ClientTagRuleType.Inactive,
            days: 90
        },
        {
            ruleId: 'inactive180',
            enabled: true,
            tagValue: t(TK.clientTagInactive180),
            color: '#FF5722',
            type: ClientTagRuleType.Inactive,
            days: 180
        },
        {
            ruleId: 'inactive365',
            enabled: true,
            tagValue: t(TK.clientTagInactive365),
            color: '#F44336',
            type: ClientTagRuleType.Inactive,
            days: 365
        },
        {
            ruleId: 'debtor',
            enabled: true,
            tagValue: t(TK.clientTagDebtor),
            color: DEFAULT_TAG_COLORS[ClientTagRuleType.Debtor],
            type: ClientTagRuleType.Debtor,
            days: 2
        }
    ];
};

export const buildInitialRules = (
    rules: ClientTagRuleSettings[] | undefined,
    t: (key: any) => string
): ClientTagRuleSettings[] => {
    const defaultRules = createDefaultRules(t);

    if (!rules || rules.length === 0) {
        return defaultRules.map(rule => ({ ...rule, enabled: false }));
    }

    const rulesById = new Map(rules.map(rule => [rule.ruleId, rule]));

    return defaultRules.map(defaultRule => {
        const existingRule = rulesById.get(defaultRule.ruleId);
        if (!existingRule) {
            return { ...defaultRule, enabled: false };
        }

        return {
            ...defaultRule,
            ...existingRule,
            color: existingRule.color || getDefaultColor(existingRule)
        };
    });
};

const RuleContainer = styled(Box)`
    margin-bottom: var(--spacer-M);
    padding: var(--spacer-S);
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: 4px;
    background-color: var(--bg-light);
`;

const RuleHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--spacer-S);
`;

const RuleTitle = styled.div`
    font-weight: 600;
    font-size: 1rem;
    color: var(--text-body);
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
`;

const FieldRow = styled.div`
    display: flex;
    gap: var(--spacer-M);
    margin-top: var(--spacer-S);
    align-items: flex-start;
    flex-wrap: wrap;

    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const TagChipContainer = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
`;

const ColorPickerDialog = styled(Dialog)``;
const DaysInputContainer = styled.div`
    flex: 1;
    min-width: 200px;
`;

interface Props {
    values: GeneralSettingsData;
    onChange: (values: GeneralSettingsData) => void;
}

const ClientsIntelligentCategorizationSettings: React.FC<Props> = ({ values, onChange }) => {
    const t = useTranslations();

    const [localSettings, setLocalSettings] = useState<CategorizationSettings>(() => {
        const settings = values?.clientIntelligentCategorization;

        return {
            ...settings,
            rules: buildInitialRules(settings?.rules, t),
            lastRunDateUtc: settings?.lastRunDateUtc
        };
    });

    const [editingTagIndex, setEditingTagIndex] = useState<number | null>(null);
    const [editingTagName, setEditingTagName] = useState<string>('');
    const [showHelpDialog, setShowHelpDialog] = useState<string | null>(null);
    const [colorPickerOpen, setColorPickerOpen] = useState<number | null>(null);
    const [tempColor, setTempColor] = useState<string>('#cccccc');

    React.useEffect(() => {
        onChange({
            ...values,
            clientIntelligentCategorization: localSettings
        });
    }, [localSettings]);

    const handleRuleChange = useCallback((index: number, field: keyof ClientTagRuleSettings, value: any) => {
        setLocalSettings(prev => {
            const newRules = [...prev.rules];
            newRules[index] = {
                ...newRules[index],
                [field]: value
            };
            return {
                ...prev,
                rules: newRules
            };
        });
    }, []);

    const getDefaultTagName = (rule: ClientTagRuleSettings): string => {
        switch (rule.type) {
            case ClientTagRuleType.Active:
                return t(TK.clientTagActive);
            case ClientTagRuleType.AtRisk:
                return t(TK.clientTagAtRisk);
            case ClientTagRuleType.Inactive:
                // Determine which inactive variant based on days
                if (rule.days <= 90) return t(TK.clientTagInactive90);
                if (rule.days <= 180) return t(TK.clientTagInactive180);
                return t(TK.clientTagInactive365);
            case ClientTagRuleType.Debtor:
                return t(TK.clientTagDebtor);
            default:
                return rule.ruleId;
        }
    };

    const getHelpText = (rule: ClientTagRuleSettings): string => {
        switch (rule.type) {
            case ClientTagRuleType.Active:
                return t(TK.activeTagHelp);
            case ClientTagRuleType.AtRisk:
                return t(TK.atRiskTagHelp);
            case ClientTagRuleType.Inactive:
                return t(TK.inactiveTagHelp);
            case ClientTagRuleType.Debtor:
                return t(TK.debtorTagHelp);
            default:
                return '';
        }
    };

    const getThresholdLabel = (rule: ClientTagRuleSettings): string => {
        if (rule.type === ClientTagRuleType.Debtor) {
            return `${t(TK.debtOutstandingFor)} (${t(TK.days).toLocaleLowerCase()})`;
        }

        return t(TK.daysSinceLastAppointment);
    };

    const handleTagNameEdit = useCallback((index: number) => {
        const rule = localSettings.rules[index];
        setEditingTagIndex(index);
        setEditingTagName(rule.tagValue || getDefaultTagName(rule));
    }, [localSettings.rules]);

    const handleTagNameSave = useCallback((index: number) => {
        if (editingTagName.trim()) {
            handleRuleChange(index, 'tagValue', editingTagName.trim());
        }
        setEditingTagIndex(null);
        setEditingTagName('');
    }, [editingTagName, handleRuleChange]);

    const handleTagNameCancel = useCallback(() => {
        setEditingTagIndex(null);
        setEditingTagName('');
    }, []);

    const handleColorEdit = useCallback((index: number) => {
        const rule = localSettings.rules[index];
        setTempColor(rule.color || getDefaultColor(rule));
        setColorPickerOpen(index);
    }, [localSettings.rules]);

    const handleColorSave = useCallback(() => {
        if (colorPickerOpen !== null) {
            handleRuleChange(colorPickerOpen, 'color', tempColor);
        }
        setColorPickerOpen(null);
    }, [colorPickerOpen, tempColor, handleRuleChange]);

    const renderRule = (rule: ClientTagRuleSettings, index: number) => {
        const isEditingName = editingTagIndex === index;
        const helpText = getHelpText(rule);
        const tagName = rule.tagValue || getDefaultTagName(rule);

        return (
            <RuleContainer key={rule.ruleId}>
                <RuleHeader>
                    <RuleTitle>
                        <TagChipContainer>
                            {isEditingName ? (
                                <ClickAwayListener onClickAway={() => handleTagNameSave(index)}>
                                    <div>
                                        <TextInputField
                                            value={editingTagName}
                                            onChange={setEditingTagName}
                                            onKeyUp={(e) => {
                                                if (e.key === 'Enter') handleTagNameSave(index);
                                                if (e.key === 'Escape') {
                                                    handleTagNameCancel();
                                                }
                                            }}
                                            disabled={!rule.enabled}
                                            style={{ width: '200px' }}
                                        />
                                    </div>
                                </ClickAwayListener>
                            ) : (
                                <>
                                    <Chip
                                        label={tagName}
                                        style={{
                                            backgroundColor: rule.color || getDefaultColor(rule),
                                            color: '#fff',
                                            fontWeight: 500,
                                            cursor: rule.enabled ? 'pointer' : 'default'
                                        }}
                                        onClick={() => rule.enabled && handleColorEdit(index)}
                                        disabled={!rule.enabled}
                                    />
                                    {rule.enabled && (
                                        <IconButton
                                            size="small"
                                            onClick={() => handleTagNameEdit(index)}
                                            style={{ padding: 'var(--spacer-XXS)' }}
                                            title={t(TK.edit)}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </>
                            )}
                        </TagChipContainer>
                        {helpText && (
                            <IconButton
                                size="small"
                                onClick={() => setShowHelpDialog(rule.ruleId)}
                                style={{ padding: 'var(--spacer-XXS)' }}
                            >
                                <HelpIcon fontSize="small" />
                            </IconButton>
                        )}
                    </RuleTitle>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={rule.enabled}
                                onChange={(e) => handleRuleChange(index, 'enabled', e.target.checked)}
                                size="small"
                            />
                        }
                        label={<MuiTypography variant="body2">{t(TK.ruleEnabled)}</MuiTypography>}
                    />
                </RuleHeader>

                <FieldRow>
                    <DaysInputContainer>
                        <TextInputField
                            id={`days-${rule.ruleId}`}
                            type="number"
                            min={1}
                            value={rule.days || ''}
                            onChange={(value) => handleRuleChange(index, 'days', parseInt(value) || 0)}
                            disabled={!rule.enabled}
                            label={getThresholdLabel(rule)}
                        />
                    </DaysInputContainer>
                </FieldRow>

            </RuleContainer>
        );
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Alert color="info" isOpen>
                {t(TK.intelligentCategorizationDescription)}
            </Alert>

            {localSettings.lastRunDateUtc && (
                <Box sx={{ marginTop: 'var(--spacer-M)', marginBottom: 'var(--spacer-M)', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                    {t(TK.lastRunDate)}: {new Date(localSettings.lastRunDateUtc).toLocaleString()}
                </Box>
            )}

            <Box sx={{ marginTop: 'var(--spacer-M)' }}>
                {localSettings.rules.map((rule, index) => renderRule(rule, index))}
            </Box>

            {/* Color Picker Dialog */}
            {colorPickerOpen !== null && (
                <ColorPickerDialog open={true} onClose={() => setColorPickerOpen(null)} maxWidth="xs">
                    <DialogTitle>{t(TK.selectColor)}</DialogTitle>
                    <DialogContent style={{ padding: 'var(--spacer-M)' }}>
                        <ChromePicker
                            color={tempColor}
                            onChange={(color) => setTempColor(color.hex)}
                            onChangeComplete={(color) => setTempColor(color.hex)}
                        />
                        <Box sx={{ marginTop: 'var(--spacer-M)', display: 'flex', gap: 'var(--spacer-S)', justifyContent: 'flex-end' }}>
                            <MuiTypography
                                variant="button"
                                onClick={() => setColorPickerOpen(null)}
                                style={{ cursor: 'pointer', color: 'var(--text-muted)' }}
                            >
                                {t(TK.cancel)}
                            </MuiTypography>
                            <MuiTypography
                                variant="button"
                                onClick={handleColorSave}
                                style={{ cursor: 'pointer', color: 'var(--appBlue)', fontWeight: 600 }}
                            >
                                {t(TK.save)}
                            </MuiTypography>
                        </Box>
                    </DialogContent>
                </ColorPickerDialog>
            )}

            {/* Help Dialog */}
            {showHelpDialog && (
                <Dialog open={true} onClose={() => setShowHelpDialog(null)} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        {(() => {
                            const rule = localSettings.rules.find(r => r.ruleId === showHelpDialog);
                            return rule ? (rule.tagValue || getDefaultTagName(rule)) : '';
                        })()}
                    </DialogTitle>
                    <DialogContent>
                        {getHelpText(localSettings.rules.find(r => r.ruleId === showHelpDialog)!)
                            .split('\n')
                            .map((line, idx) => line.trim().startsWith('-')
                                ? <li key={idx}>{line.substring(line.indexOf('-') + 1).trim()}</li>
                                : <div key={idx} style={{ marginBottom: line ? 'var(--spacer-XS)' : 0 }}>{line}</div>
                            )}
                    </DialogContent>
                </Dialog>
            )}
        </Box>
    );
};

export default ClientsIntelligentCategorizationSettings;
